
/**
 * Module dependencies.
 */

import Debug from 'debug'
import Emitter from 'component-emitter'
import request from 'superagent'

/**
 * Debug.
 */

const debug = new Debug('lib:stripe')

/**
 * Expose `Stripe`.
 */

export default Stripe

/**
 * Light-weight wrapper around `StripeCheckout` with
 * a less awkward API.
 *
 * Config (see https://stripe.com/docs/checkout#integration-custom):
 *
 *   - workspaceSlug: the workspace slug
 *   - email: billing email
 *   - key (optional): stripe key (default `window.segment.stripe`)
 *   - name: (optional) company name (default: "Segment")
 *   - description (optional): product description (default: "Your
 *     analytics data platform")
 *   - panelLabel (optional): button label (default: "Subscribe")
 *
 * @param {Object} config
 * @api public
 */

function Stripe (config) {
  if (!(this instanceof Stripe)) {
    return new Stripe(config)
  }

  debug('Stripe(%j)', config)

  this.workspaceSlug = config.workspaceSlug
  delete config.workspaceSlug

  // defaults
  if (!config.key) config.key = window.segment.stripe
  if (!config.name) config.name = 'Segment'
  if (!config.description) config.description = 'Your analytics data platform'
  if (!config.panelLabel) config.panelLabel = 'Subscribe'
  this.config = config

  this.config.zipCode = true
  this.config.billingAddress = true

  // for testing
  this.s = window.StripeCheckout
  Emitter.call(this)
}

/**
 * Mixin `Emitter`.
 */

Emitter(Stripe.prototype)

/**
 * Open the Stripe dialog and get a "token".  Invokes
 * `fn(err, token)` when done.
 *
 * Events:
 *
 *   - open: the dialog has opened
 *   - close: the dialog has closed
 *   - token: a token has been created
 *
 * @param {Function}
 * @api public
 */

Stripe.prototype.getToken = function (fn) {
  const opts = {
    ...this.config,
    opened: this.onOpened.bind(this),
    closed: this.onClosed.bind(this),
    token: this.onToken.bind(this, fn)
  }
  debug('StripeCheckout.open(%j)', opts)
  this.s.open(opts)
}

/**
 * Set the workspace slug (for stripe billing)
 * for signup flow, we don't have the slug when we call getToken
 * so this should be called _after_ workspace is created but get getToken is invoked
 * @param {String} newSlug the workspace slug
 */

Stripe.prototype.setWorkspaceSlug = function (newSlug) {
  this.workspaceSlug = newSlug
}

/**
 * Set the billing email address (for stripe billing)
 * for signup flow, invoke this so stripe modal doesnt require user to re-input their email address
 * @param {String} email the workspace billing email
 */

Stripe.prototype.setBillingEmail = function (email) {
  this.config.email = email
}

/**
 * Add a card, then invoke `fn(err, card)`.  If no `token`
 * is provided, will get one from stripe first.
 *
 * Events:
 *
 *   - "card": a card has been added
 *
 * @see {Stripe#getToken}
 * @param {String} [token]
 * @param {Function} fn
 * @api public
 */

Stripe.prototype.addCard = function (token, fn) {
  const self = this
  const workspace = this.workspaceSlug

  if (typeof token === 'function') {
    fn = token
    this.getToken(addCard)
  } else {
    addCard(null, token)
  }

  // add card using "token"
  function addCard (err, token) {
    if (err) return fn(err)

    // Track state of `addCard` to be used later when StripeCheckout is closed
    self._addingCard = true
    request
    .put(`/api/workspaces/${workspace}/billing/card`)
    .send({ token: token })
    .set('x-csrf-token', window.segment.csrf)
    .end(function (err, res) {
      if (err || !res.ok) {
        debug(err)
        return fn(err)
      }

      self._addingCard = false

      self.emit('card', res)
      debug('card: %j', res)
      fn(null, res)
    })
  }
}

/**
 * Stripe "token" callback.
 *
 * @param {Function} fn
 * @param {Object} res
 * @api private
 */

Stripe.prototype.onToken = function (fn, res) {
  debug('token: %j', res)
  const token = res.id
  this.emit('token', token)
  fn(null, token)
}

/**
 * Stripe "opened" callback.
 *
 * @api private
 */

Stripe.prototype.onOpened = function () {
  debug('open')
  this.emit('open')
}

/**
 * Stripe "closed" callback.
 *
 * @api private
 */

Stripe.prototype.onClosed = function () {
  debug('close')
  this.emit('close')

  // 'cancelled' will only be emitted if the user has closed the checkout form.
  // This is inferred from whether or not the `addCard` callback has fired.
  if (!this._addingCard) this.emit('cancelled')
}



// WEBPACK FOOTER //
// ./lib/stripe.js